import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, ArrowLeftRight, Calendar, Wallet, ShoppingBag, Repeat, Gavel, Trophy, Play } from 'lucide-react';
import CoachTrainingModal from './CoachTrainingModal';
import { cardsApi } from '../../api/cards';
import { marketplaceApi } from '../../api/marketplace';
import { CardImage, getCustomPlayerImageUrl } from '../../utils/cardImageUtils';
import { getTeamAbbr } from '../../utils/teamAbbr';
import { formatDate, formatDateTime } from '../../utils/locale';
import { isNotCalledUpMatch } from '../../utils/matchCallUpUtils';
import { useLanguage } from '../../i18n/LanguageContext';
import { useConfirmSpendD10S } from '../../context/ConfirmD10SContext';

const CardItem = ({ card, isUsedInActiveGameweek, isInCoachTraining = false, user, onCardListed, hasOpenTrade, openTrade, hideHoverActions = false, hideSellCart = false }) => {
  const [showSaleModal, setShowSaleModal] = useState(false);
  const { locale, t } = useLanguage();
  const { confirm } = useConfirmSpendD10S();
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showCoachTrainingModal, setShowCoachTrainingModal] = useState(false);
  const [cardWithNextMatch, setCardWithNextMatch] = useState(card);
  const [loadingNextMatch, setLoadingNextMatch] = useState(false);
  const [playerScoreStats, setPlayerScoreStats] = useState(null);
  const [loadingPlayerScoreStats, setLoadingPlayerScoreStats] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [loanPricePerGW, setLoanPricePerGW] = useState('');
  const [loanDurationGW, setLoanDurationGW] = useState(1);
  const [loanPricePerPeriod, setLoanPricePerPeriod] = useState('');
  const [loanPeriodCount, setLoanPeriodCount] = useState(4);
  const COACH_LOAN_PERIOD_HOURS = 6;
  const [auctionStartingPrice, setAuctionStartingPrice] = useState('');
  const [auctionDurationHours, setAuctionDurationHours] = useState(24);
  const [auctionDurationMinutes, setAuctionDurationMinutes] = useState(0);
  const [auctionDurationSeconds, setAuctionDurationSeconds] = useState(0);
  const [isListing, setIsListing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [tradeCardSearch, setTradeCardSearch] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedRarity, setSelectedRarity] = useState('');
  const [searchingPlayers, setSearchingPlayers] = useState(false);
  const [playerResults, setPlayerResults] = useState([]);
  
  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  
  // Fetch card with matches when opening info modal (on-demand)
  const handleOpenInfoModal = async () => {
    setShowInfoModal(true);
    setCardWithNextMatch(card);
    setPlayerScoreStats(null);

    const playerId = card?.player?.id ?? card?.player_id_from_players ?? card?.player_id;
    const scoreStatsPromise =
      playerId != null && !Number.isNaN(Number(playerId))
        ? (async () => {
            setLoadingPlayerScoreStats(true);
            try {
              const res = await cardsApi.getPlayerRecentScores(Number(playerId), 5);
              if (res?.success && res.available) {
                setPlayerScoreStats({
                  available: true,
                  average: res.average,
                  matchCount: res.matchCount,
                  scores: res.scores || [],
                });
              }
            } catch (error) {
              console.error('Error fetching player score stats:', error);
            } finally {
              setLoadingPlayerScoreStats(false);
            }
          })()
        : Promise.resolve();

    setLoadingNextMatch(true);
    try {
      const [response] = await Promise.all([
        cardsApi.getNextMatch(card.id, card.template_id, card.rarity?.name),
        scoreStatsPromise,
      ]);
      if (response?.success) {
        setCardWithNextMatch({
          ...card,
          next_match: response.next_match || null,
          in_progress_match: response.in_progress_match || null,
          last_match: response.last_match || null
        });
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoadingNextMatch(false);
    }
  };
  
  // Calculate age and format date of birth
  const getDateOfBirth = () => {
    const dob = cardWithNextMatch.player?.date_of_birth;
    if (!dob) return null;
    
    // Handle different date formats
    let date;
    if (typeof dob === 'string') {
      // Try parsing as ISO date or other formats
      date = new Date(dob);
      if (isNaN(date.getTime())) {
        // Try parsing as YYYY-MM-DD
        const parts = dob.split('-');
        if (parts.length === 3) {
          date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        }
      }
    } else if (dob instanceof Date) {
      date = dob;
    } else {
      return null;
    }
    
    if (isNaN(date.getTime())) return null;
    return date;
  };
  
  const birthDate = getDateOfBirth();
  const playerAge = birthDate ? calculateAge(birthDate) : null;
  const dateOfBirth = birthDate ? formatDate(birthDate, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }, locale) : 'N/A';
  const nationality = cardWithNextMatch.player?.nationality || 'N/A';
  const nextMatch = cardWithNextMatch.next_match;
  const inProgressMatch = cardWithNextMatch.in_progress_match;
  const lastMatch = cardWithNextMatch.last_match;

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'basic': return 'from-gray-400 to-gray-600';
      case 'plus': return 'from-blue-400 to-blue-600';
      case 'rare': return 'from-purple-400 to-purple-600';
      case 'epic': return 'from-orange-400 to-orange-600';
      case 'unique': return 'from-yellow-400 to-yellow-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getRarityBadgeColor = (rarity) => {
    switch (rarity) {
      case 'basic': return 'bg-gray-500/20 text-gray-300 border-gray-500';
      case 'plus': return 'bg-blue-500/20 text-blue-300 border-blue-500';
      case 'rare': return 'bg-purple-500/20 text-purple-300 border-purple-500';
      case 'epic': return 'bg-orange-500/20 text-orange-300 border-orange-500';
      case 'unique': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500';
    }
  };

  // Map position code or Italian label to English for display
  const positionToEnglish = (pos) => {
    if (!pos) return '';
    const p = String(pos).toUpperCase();
    const map = {
      GK: 'Goalkeeper',
      DEF: 'Defender',
      MID: 'Midfielder',
      ATT: 'Attacker',
      PORTIERE: 'Goalkeeper',
      DIFENSORE: 'Defender',
      CENTROCAMPISTA: 'Midfielder',
      ATTACCANTE: 'Attacker',
    };
    return map[p] || map[pos] || pos;
  };
  // Short form for small badge (top-right on card): GK, DEF, MID, ATT
  const positionToEnglishShort = (pos) => {
    if (!pos) return '';
    const p = String(pos).toUpperCase();
    const map = { GK: 'GK', DEF: 'DEF', MID: 'MID', ATT: 'ATT', PORTIERE: 'GK', DIFENSORE: 'DEF', CENTROCAMPISTA: 'MID', ATTACCANTE: 'ATT' };
    return map[p] || map[pos] || (p.length <= 3 ? p : positionToEnglish(pos));
  };

  const rarityName = card.rarity?.name || 'basic';
  const normalizedCardKind = String(card.cardKind || card.card_kind || '').toLowerCase();
  const hasCustomTemplateImage = !!(card.customImagePath || card.custom_image_path);
  const hasNoRealPlayerId = card.player_id == null && card.player?.id == null;
  const disableProfileImageZoom =
    normalizedCardKind === 'legend' ||
    normalizedCardKind === 'divinity' ||
    (hasCustomTemplateImage && hasNoRealPlayerId);
  const xpRaw = card.xp_percentage ?? card.xpPercentage ?? 0;
  const xpPercentage = typeof xpRaw === 'number' ? xpRaw : parseFloat(xpRaw) || 0;
  // Ogni 1.5% = 1 livello, max 10 (15% XP massimo)
  const xpLevel = Math.min(10, Math.floor(xpPercentage / 1.5));
  const maxLevel = 10;
  const xpMaxPercent = 15; // scala progress bar: 15% = barra piena

  const isLoan = card.loan && card.loan.status === 'active';
  const coachLoanKind = String(card.loan?.loan_kind || card.loan_kind || '').toLowerCase();
  const loanDurationGw = card.loan?.duration_gw ?? 0;
  const loanDeploymentsCount = card.loan?.deployments_count ?? 0;
  const remainingGW =
    isLoan && loanDurationGw > 0
      ? Math.max(0, loanDurationGw - loanDeploymentsCount)
      : null;
  const isInSale = card.is_in_sale;
  const isInAuction = card.is_in_auction;
  const isOnLoan = card.is_on_loan;
  // Check if user owns the card (not borrowed)
  // In profile, when admin use linked user for ownership/sale/loan; trades always use user.id
  const cardOwnerId = typeof card.owner_id === 'string' ? parseInt(card.owner_id) : card.owner_id;
  const profileUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;
  const profileUserIdNum = typeof profileUserId === 'string' ? parseInt(profileUserId, 10) : profileUserId;
  const isOwner = cardOwnerId === profileUserIdNum;
  const isOwnerSelfCoachLoan =
    isOwner && isLoan && coachLoanKind === 'owner_self';
  // Prestito marketplace (borrower). owner_self = uso allenatore del proprietario, non un vero prestito.
  const isBorrowed = isLoan && !isOwnerSelfCoachLoan && card.acquired_via === 'loan';
  const isBorrowedLegendCoach =
    isBorrowed &&
    normalizedCardKind === 'legend' &&
    (coachLoanKind === 'period' || coachLoanKind === '');
  const coachLoanId = card.loan?.id;
  const activeCoachLoanId =
    (isBorrowedLegendCoach || isOwnerSelfCoachLoan) && coachLoanId ? coachLoanId : null;
  const isOwnerLegendCoach =
    isOwner && normalizedCardKind === 'legend' && !isBorrowedLegendCoach;
  // User can only list cards they own, that are not in use, not borrowed, and not already listed
  const isBlockedForListing = isUsedInActiveGameweek || (isInCoachTraining && !isOwnerLegendCoach);
  const canList = isOwner && !isBorrowed && !isBlockedForListing && !isInSale && !isInAuction && !isOnLoan && !isLoan;
  const canUseOwnCoach =
    isOwnerLegendCoach &&
    !isUsedInActiveGameweek &&
    !isInSale &&
    !isInAuction &&
    !isOnLoan &&
    (!isLoan || isOwnerSelfCoachLoan);

  const handleCreateSale = async () => {
    if (!salePrice || parseFloat(salePrice) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid price' });
      return;
    }

    // Double check ownership before listing
    if (!isOwner) {
      setMessage({ type: 'error', text: '❌ You do not own this card. Only the owner can list it for sale.' });
      return;
    }

    const priceNum = parseFloat(salePrice);
    const ok = await confirm(priceNum, t('confirmD10S.actionListSale'), {
      firstMessage: t('confirmD10S.firstListSale', { amount: priceNum.toFixed(2) })
    });
    if (!ok) return;

    setIsListing(true);
    try {
      await marketplaceApi.createSale({
        cardId: card.id,
        templateId: card.template_id,
        sellerId: profileUserIdNum,
        price: parseFloat(salePrice)
      });
      setMessage({ type: 'success', text: '✅ Card listed for sale successfully!' });
      setShowSaleModal(false);
      setSalePrice('');
      onCardListed();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to list card for sale'}` });
    } finally {
      setIsListing(false);
    }
  };

  const handleCreateLoan = async () => {
    if (!isOwner) {
      setMessage({ type: 'error', text: '❌ You do not own this card. Only the owner can list it for loan.' });
      return;
    }

    const isLegendLoan = normalizedCardKind === 'legend';

    if (isLegendLoan) {
      const price = parseFloat(loanPricePerPeriod);
      const periods = parseInt(loanPeriodCount, 10);
      if (!loanPricePerPeriod || !Number.isFinite(price) || price <= 0) {
        setMessage({ type: 'error', text: 'Inserisci un prezzo valido per periodo (6h)' });
        return;
      }
      if (!Number.isFinite(periods) || periods < 1 || periods > 48) {
        setMessage({ type: 'error', text: 'Il numero di periodi deve essere tra 1 e 48' });
        return;
      }
      const slotTotal = price;
      const ok = await confirm(slotTotal, t('confirmD10S.actionListLoan'), {
        firstMessage: `Listing: ${periods} cicli da ${COACH_LOAN_PERIOD_HOURS}h (ogni utente ne prende 1 alla volta). Prezzo per singolo periodo: ${slotTotal.toFixed(2)} D10S.`
      });
      if (!ok) return;

      setIsListing(true);
      try {
        await marketplaceApi.createLoan({
          cardId: card.id,
          templateId: card.template_id,
          lenderId: profileUserIdNum,
          pricePerPeriod: price,
          periodCount: periods
        });
        setMessage({ type: 'success', text: '✅ Card in prestito (periodi 6h) creata!' });
        setShowLoanModal(false);
        setLoanPricePerPeriod('');
        setLoanPeriodCount(4);
        onCardListed();
      } catch (error) {
        setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to list card for loan'}` });
      } finally {
        setIsListing(false);
      }
      return;
    }

    if (!loanPricePerGW || parseFloat(loanPricePerGW) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid price per gameweek' });
      return;
    }

    if (![1, 3, 5, 10].includes(loanDurationGW)) {
      setMessage({ type: 'error', text: 'Duration must be 1, 3, 5, or 10 gameweeks' });
      return;
    }

    const totalLoan = parseFloat(loanPricePerGW) * loanDurationGW;
    const ok = await confirm(totalLoan, t('confirmD10S.actionListLoan'), {
      firstMessage: t('confirmD10S.firstListLoan', { amount: totalLoan.toFixed(2) })
    });
    if (!ok) return;

    setIsListing(true);
    try {
      await marketplaceApi.createLoan({
        cardId: card.id,
        templateId: card.template_id,
        lenderId: profileUserIdNum,
        pricePerGW: parseFloat(loanPricePerGW),
        durationGW: loanDurationGW
      });
      setMessage({ type: 'success', text: '✅ Card listed for loan successfully!' });
      setShowLoanModal(false);
      setLoanPricePerGW('');
      setLoanDurationGW(1);
      onCardListed();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to list card for loan'}` });
    } finally {
      setIsListing(false);
    }
  };

  const handleCreateAuction = async () => {
    if (!auctionStartingPrice || parseFloat(auctionStartingPrice) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid starting price' });
      return;
    }

    // Double check ownership before listing
    if (!isOwner) {
      setMessage({ type: 'error', text: '❌ You do not own this card. Only the owner can create an auction.' });
      return;
    }

    const totalSeconds = (parseInt(auctionDurationHours, 10) * 3600)
      + (parseInt(auctionDurationMinutes, 10) * 60)
      + parseInt(auctionDurationSeconds, 10);
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
      setMessage({ type: 'error', text: 'Please set an auction duration greater than 0 seconds.' });
      return;
    }

    setIsListing(true);
    try {
      await marketplaceApi.createAuction({
        cardId: card.id,
        sellerId: profileUserIdNum,
        startingPrice: parseFloat(auctionStartingPrice),
        durationHours: auctionDurationHours,
        durationMinutes: auctionDurationMinutes,
        durationSeconds: auctionDurationSeconds
      });
      setMessage({ type: 'success', text: '✅ Auction created successfully!' });
      setShowAuctionModal(false);
      setAuctionStartingPrice('');
      setAuctionDurationHours(24);
      setAuctionDurationMinutes(0);
      setAuctionDurationSeconds(0);
      onCardListed();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to create auction'}` });
    } finally {
      setIsListing(false);
    }
  };

  const handleSearchPlayers = async () => {
    if (!tradeCardSearch.trim()) {
      setMessage({ type: 'error', text: 'Please enter a player name' });
      return;
    }

    setSearchingPlayers(true);
    try {
      const response = await cardsApi.searchPlayers(tradeCardSearch.trim());
      if (response?.success && response?.players) {
        setPlayerResults(response.players);
        if (response.players.length === 0) {
          setMessage({ type: 'error', text: 'No players found' });
        }
      } else {
        setMessage({ type: 'error', text: 'No players found' });
        setPlayerResults([]);
      }
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to search players'}` });
      setPlayerResults([]);
    } finally {
      setSearchingPlayers(false);
    }
  };

  const handleCreateTrade = async () => {
    if (!selectedPlayer || !selectedRarity) {
      setMessage({ type: 'error', text: 'Please select a player and rarity' });
      return;
    }

    // Double check ownership before creating trade
    if (!isOwner) {
      setMessage({ type: 'error', text: '❌ You do not own this card. Only the owner can create a trade.' });
      return;
    }

    setIsListing(true);
    try {
      await marketplaceApi.createTrade({
        user1Id: profileUserIdNum,
        card1Id: card.id,
        desiredPlayerId: selectedPlayer.id,
        desiredRarity: selectedRarity
      });
      setMessage({ type: 'success', text: '✅ Open trade created successfully! Anyone with the requested card can accept it.' });
      setShowTradeModal(false);
      setTradeCardSearch('');
      setSelectedPlayer(null);
      setSelectedRarity('');
      setPlayerResults([]);
      onCardListed();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to create trade'}` });
    } finally {
      setIsListing(false);
    }
  };

  const handleRemoveTrade = async () => {
    if (!openTrade || !openTrade.id) {
      setMessage({ type: 'error', text: 'Trade information not found' });
      return;
    }

    setIsListing(true);
    try {
      // Check if it's a counter offer or an open trade
      if (openTrade.isCounterOffer) {
        await marketplaceApi.cancelCounterOffer(openTrade.id, user.id);
        setMessage({ type: 'success', text: '✅ Counter offer removed successfully!' });
      } else {
        await marketplaceApi.cancelTrade(openTrade.id, user.id);
        setMessage({ type: 'success', text: '✅ Trade removed successfully!' });
      }
      setShowTradeModal(false);
      onCardListed();
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message || 'Failed to remove trade'}` });
    } finally {
      setIsListing(false);
    }
  };

  return (
    <div className={`group profile-card-item football-card glass-football card-hover card-rarity-${rarityName} ${disableProfileImageZoom ? 'profile-card-no-image-zoom' : ''} relative overflow-hidden w-[303px] h-[452px] min-w-[303px] min-h-[452px] p-0 border-0 rounded-none box-border ${isBlockedForListing ? 'opacity-75 ring-2 ring-red-500/50' : ''} ${isLoan ? 'ring-2 ring-yellow-500/50' : ''}`}>
      {/* Standard card artwork uses a scaled frame; custom legend/divinity keeps original full image without crop */}
      <div className={`absolute inset-0 z-0 overflow-hidden flex items-center justify-center card-image-rarity-${rarityName} ${disableProfileImageZoom ? 'profile-card-image-shell-no-zoom' : ''} ${isBlockedForListing ? 'opacity-80' : ''}`}>
        <div
          className={`flex-shrink-0 ${disableProfileImageZoom ? 'profile-card-image-frame-no-zoom' : ''}`}
          style={disableProfileImageZoom
            ? undefined
            : { width: (300 * 300) / 222, height: (450 * 300) / 222, transform: 'translateY(8px)' }}
        >
          <CardImage
            card={card}
            alt={card.player?.full_name || 'Card'}
            className={`w-full h-full ${disableProfileImageZoom ? 'object-contain' : 'object-cover'}`}
          />
        </div>
      </div>

      {/* Top left: logo piattaforma */}
      <div
        className="absolute top-0 left-0 z-20 flex items-center justify-center pointer-events-none"
        style={{
          marginLeft: '-1%',
          marginTop: '-3.5%',
          height: '17.5%',
          aspectRatio: '1 / 1',
          minWidth: '5.5%',
        }}
      >
        <img src="/images/logo.png" alt="DIOS10" className="w-full h-full object-contain" />
      </div>

      {/* Top right: position (quadrato come nel design della card) - always in English */}
      {card.player?.position && (
        <div
          className="absolute top-0 right-0 z-20 flex items-center justify-center text-white text-xs font-bold uppercase tracking-wide pointer-events-none"
          style={{
            marginRight: '5.7%',
            marginTop: '3%',
            height: '8.5%',
            aspectRatio: '1 / 1',
            minWidth: '7.5%',
            color: '#fff',
            WebkitTextStroke: '1.5px black',
            paintOrder: 'stroke fill',
          }}
        >
          {positionToEnglishShort(card.player?.position)}
        </div>
      )}

      {/* Legend: Use + Loan (proprietario) o solo Use (prestito). Altre card: Info + Sell + Loan */}
      {!hideHoverActions && (
      <div className="absolute inset-0 z-30 flex opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200">
        {normalizedCardKind === 'legend' ? (
          isBorrowedLegendCoach && coachLoanId ? (
            <button
              type="button"
              onClick={() => setShowCoachTrainingModal(true)}
              className="w-full h-full flex flex-col items-center justify-center gap-2 bg-emerald-600/90 hover:bg-emerald-600 transition-colors text-white"
              title={t('coachTraining.useTitle')}
            >
              <Play className="w-16 h-16 sm:w-20 sm:h-20" strokeWidth={2.5} />
              <span className="text-sm font-bold uppercase tracking-wide">{t('coachTraining.use')}</span>
            </button>
          ) : isOwnerLegendCoach ? (
            <>
              {canUseOwnCoach ? (
                <button
                  type="button"
                  onClick={() => setShowCoachTrainingModal(true)}
                  className="flex-1 flex flex-col items-center justify-center gap-2 bg-emerald-600/90 hover:bg-emerald-600 transition-colors text-white min-h-0"
                  title={t('coachTraining.useTitle')}
                >
                  <Play className="w-12 h-12 sm:w-14 sm:h-14" strokeWidth={2.5} />
                  <span className="text-sm font-bold uppercase tracking-wide">{t('coachTraining.use')}</span>
                </button>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center gap-2 bg-emerald-600/25 text-white/40 pointer-events-none min-h-0">
                  <Play className="w-12 h-12 sm:w-14 sm:h-14" strokeWidth={2.5} />
                  <span className="text-sm font-semibold uppercase tracking-wide">{t('coachTraining.use')}</span>
                </div>
              )}
              {canList ? (
                <button
                  type="button"
                  onClick={() => setShowLoanModal(true)}
                  className="flex-1 flex flex-col items-center justify-center gap-2 bg-blue-600/90 hover:bg-blue-600 transition-colors text-white min-h-0"
                  style={{ clipPath: 'polygon(8% 0, 100% 0, 100% 100%, 0 100%)' }}
                  title="Loan this card"
                >
                  <Repeat className="w-12 h-12 sm:w-14 sm:h-14" strokeWidth={2.5} />
                  <span className="text-sm font-bold uppercase tracking-wide">Loan</span>
                </button>
              ) : (
                <div
                  className="flex-1 flex flex-col items-center justify-center gap-2 bg-blue-600/25 text-white/40 pointer-events-none min-h-0"
                  style={{ clipPath: 'polygon(8% 0, 100% 0, 100% 100%, 0 100%)' }}
                >
                  <Repeat className="w-12 h-12 sm:w-14 sm:h-14" strokeWidth={2.5} />
                  <span className="text-sm font-semibold uppercase tracking-wide">Loan</span>
                </div>
              )}
            </>
          ) : canList ? (
            <button
              type="button"
              onClick={() => setShowLoanModal(true)}
              className="w-full h-full flex flex-col items-center justify-center gap-2 bg-blue-600/90 hover:bg-blue-600 transition-colors text-white"
              title="Loan this card"
            >
              <Repeat className="w-16 h-16 sm:w-20 sm:h-20" strokeWidth={2.5} />
              <span className="text-sm font-bold uppercase tracking-wide">Loan</span>
            </button>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-blue-600/25 text-white/40 pointer-events-none">
              <Repeat className="w-16 h-16 sm:w-20 sm:h-20" strokeWidth={2.5} />
              <span className="text-sm font-semibold uppercase tracking-wide">Loan</span>
            </div>
          )
        ) : (
          <>
            <button
              onClick={handleOpenInfoModal}
              className={`${hideSellCart ? 'w-full' : 'flex-1'} flex flex-col items-center justify-center gap-2 bg-black/40 hover:bg-blue-600/50 transition-colors text-white`}
              title="Player Info"
            >
              <Info className="w-16 h-16 sm:w-20 sm:h-20" strokeWidth={2.5} />
              <span className="text-sm font-semibold uppercase tracking-wide">Info</span>
            </button>
            {!hideSellCart && (
              <div className="flex-1 flex flex-col min-h-0 min-w-0">
                {canList ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowSaleModal(true)}
                      className="flex-1 flex flex-col items-center justify-center gap-2 bg-black/55 hover:bg-black/75 transition-colors text-white min-h-0 w-full"
                      title="Sell this card"
                    >
                      <ShoppingBag className="w-12 h-12 sm:w-14 sm:h-14" strokeWidth={2.5} />
                      <span className="text-sm font-bold uppercase tracking-wide">Sell</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowLoanModal(true)}
                      className="flex-1 flex flex-col items-center justify-center gap-2 bg-blue-600/90 hover:bg-blue-600 transition-colors text-white min-h-0 w-full"
                      style={{ clipPath: 'polygon(8% 0, 100% 0, 100% 100%, 0 100%)' }}
                      title="Loan this card"
                    >
                      <Repeat className="w-12 h-12 sm:w-14 sm:h-14" strokeWidth={2.5} />
                      <span className="text-sm font-bold uppercase tracking-wide">Loan</span>
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 flex flex-col items-center justify-center gap-2 bg-black/25 text-white/40 pointer-events-none min-h-0">
                      <ShoppingBag className="w-12 h-12 sm:w-14 sm:h-14" strokeWidth={2.5} />
                      <span className="text-sm font-semibold uppercase">Sell</span>
                    </div>
                    <div
                      className="flex-1 flex flex-col items-center justify-center gap-2 bg-blue-600/25 text-white/40 pointer-events-none min-h-0"
                      style={{ clipPath: 'polygon(8% 0, 100% 0, 100% 100%, 0 100%)' }}
                    >
                      <Repeat className="w-12 h-12 sm:w-14 sm:h-14" strokeWidth={2.5} />
                      <span className="text-sm font-semibold uppercase">Loan</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
      )}
      
      {hasOpenTrade && (
        <div className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded z-10 shadow-lg flex items-center gap-1">
          <ArrowLeftRight className="w-3 h-3" />
          Trade Open
        </div>
      )}
      {isUsedInActiveGameweek && (
        <div className="absolute top-2 right-2 z-10 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow-lg">
          In Uso
        </div>
      )}
      {isInCoachTraining && !isUsedInActiveGameweek && (
        <div className="absolute top-2 right-2 z-10 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow-lg">
          {t('coachTraining.badge')}
        </div>
      )}
      {isInCoachTraining && isUsedInActiveGameweek && (
        <div className="absolute top-10 right-2 z-10 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow-lg">
          {t('coachTraining.badge')}
        </div>
      )}
      {isLoan && !isOwnerSelfCoachLoan && (
        <div className={`absolute ${hasOpenTrade ? 'top-10 left-2' : 'top-2 left-2'} bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded z-10 shadow-lg`}>
          LOAN - {loanDurationGw > 0
            ? `${loanDeploymentsCount}/${loanDurationGw} GW`
            : 'Active'}
          {remainingGW !== null && remainingGW > 0 ? ` (${remainingGW} da schierare)` : ''}
        </div>
      )}

      {/* Nome sotto la scritta rarità (rarità già sull’immagine, non mostrata dalla piattaforma) - area sopra il contenuto */}

      {/* Status badges: non bloccano click sull'overlay hover */}
      {(!!isInSale || !!isInAuction || (!!isOnLoan && !isBorrowed)) && (
      <div className="absolute bottom-14 left-0 right-0 z-20 flex flex-col gap-1 px-2 pointer-events-none">
      {!!isInSale && (
        <div className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs font-semibold rounded text-center">
          💰 Listed for Sale
        </div>
      )}
      {!!isInAuction && (
        <div className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs font-semibold rounded text-center">
          🎯 In Auction
        </div>
      )}
      {!!isOnLoan && !isBorrowed && (
        <div className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-semibold rounded text-center">
          🔄 Listed for Loan
        </div>
      )}
      </div>
      )}

      {/* Nome in fondo alla card - nascosto per legend/divinity custom */}
      {!disableProfileImageZoom && (
      <div
        className="profile-card-name-bar absolute bottom-0 left-0 right-0 z-20 text-center text-white px-2 pointer-events-none font-bold"
        style={{ color: '#fff', WebkitTextStroke: '1.5px black', paintOrder: 'stroke fill', paddingBottom: '2.375rem' }}
      >
        <div
          className="profile-card-player-name text-lg"
          style={rarityName === 'rare' ? { transform: 'translateY(10px)' } : undefined}
        >
          {card.player?.full_name || 'Player'}
        </div>
        {card.player?.team && (
          <div
            className="profile-card-team-abbr text-xs absolute text-center"
            style={{
              bottom: 0,
              left: '30%',
              width: '40%',
              color: '#fff',
              WebkitTextStroke: '2px black',
              paintOrder: 'stroke',
              background: 'rgb(255 255 255 / 80%)',
              border: 'none',
              boxShadow: '1.1px 1.1px 1.1px 1.1px rgb(0, 0, 0)'
            }}
          >
            {getTeamAbbr(card.player.team)}
          </div>
        )}
      </div>
      )}

      {/* Sale Modal */}
      {showSaleModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="football-card glass-football p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">Sell Card</h2>
            <div className="mb-4">
              <p className="text-gray-300 mb-2">{card.player?.full_name || 'Card'}</p>
              <label className="block text-gray-300 mb-2">Price (DIOS)</label>
              <input
                type="number"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                placeholder="Enter price"
                className="input-football w-full"
                min="0.01"
                step="0.01"
              />
              <p className="text-xs text-gray-400 mt-2">10% fee will be deducted from sale</p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleCreateSale}
                disabled={isListing}
                className="btn-football flex-1"
              >
                {isListing ? 'Listing...' : 'List for Sale'}
              </button>
              <button
                onClick={() => {
                  setShowSaleModal(false);
                  setSalePrice('');
                }}
                className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <CoachTrainingModal
        open={showCoachTrainingModal}
        onClose={() => setShowCoachTrainingModal(false)}
        loanId={activeCoachLoanId ?? undefined}
        coachCardId={isOwnerLegendCoach && !activeCoachLoanId ? card.id : undefined}
        userId={profileUserIdNum}
        coachName={card.customName || card.custom_name}
        onStarted={onCardListed}
      />

      {/* Loan Modal */}
      {showLoanModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className={`football-card glass-football max-w-md w-full ${normalizedCardKind === 'legend' ? 'p-4' : 'p-6'}`}>
            <h2 className={`text-2xl font-bold text-[#1e293b] ${normalizedCardKind === 'legend' ? 'mb-2' : 'mb-4'}`}>
              {normalizedCardKind === 'legend' ? 'Prestito card (periodi 6h)' : 'Loan Card'}
            </h2>
            <div className={normalizedCardKind === 'legend' ? 'mb-3' : 'mb-4'}>
              <p className="text-gray-300 mb-2 truncate">
                {card.customName || card.custom_name || card.player?.full_name || 'Card'}
              </p>
              {normalizedCardKind === 'legend' ? (
                <>
                  <p className="text-xs text-gray-400 mb-2 leading-snug">
                    6h per periodo · 1 periodo per utente · cicli = slot marketplace · timer all’uso
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-gray-300 mb-2">Prezzo per periodo ({COACH_LOAN_PERIOD_HOURS}h) — DIOS</label>
                      <input
                        type="number"
                        value={loanPricePerPeriod}
                        onChange={(e) => setLoanPricePerPeriod(e.target.value)}
                        placeholder="Prezzo per periodo"
                        className="input-football w-full"
                        min="0.01"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Cicli totali sul listing</label>
                      <input
                        type="number"
                        value={loanPeriodCount}
                        onChange={(e) => setLoanPeriodCount(parseInt(e.target.value, 10) || 1)}
                        className="input-football w-full"
                        min={1}
                        max={48}
                      />
                    </div>
                  </div>
                  {loanPricePerPeriod && loanPeriodCount > 0 && (
                    <p className="text-xs text-gray-400 mt-2">
                      {loanPeriodCount}×{COACH_LOAN_PERIOD_HOURS}h totali · {(parseFloat(loanPricePerPeriod) || 0).toFixed(2)} D10S per periodo
                    </p>
                  )}
                </>
              ) : (
                <>
                  <label className="block text-gray-300 mb-2">Price per Gameweek (DIOS)</label>
                  <input
                    type="number"
                    value={loanPricePerGW}
                    onChange={(e) => setLoanPricePerGW(e.target.value)}
                    placeholder="Enter price per GW"
                    className="input-football w-full mb-4"
                    min="0.01"
                    step="0.01"
                  />
                  <label className="block text-gray-300 mb-2">Duration (Gameweeks)</label>
                  <select
                    value={loanDurationGW}
                    onChange={(e) => setLoanDurationGW(parseInt(e.target.value))}
                    className="input-football w-full"
                  >
                    <option value={1}>1 Gameweek</option>
                    <option value={3}>3 Gameweeks</option>
                    <option value={5}>5 Gameweeks</option>
                    <option value={10}>10 Gameweeks</option>
                  </select>
                  {loanPricePerGW && loanDurationGW && (
                    <p className="text-xs text-gray-400 mt-2">
                      Total: {(parseFloat(loanPricePerGW) * loanDurationGW).toFixed(2)} DIOS (10% fee included)
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleCreateLoan}
                disabled={isListing}
                className="btn-football flex-1"
              >
                {isListing ? 'Listing...' : 'List for Loan'}
              </button>
              <button
                onClick={() => {
                  setShowLoanModal(false);
                  setLoanPricePerGW('');
                  setLoanDurationGW(1);
                  setLoanPricePerPeriod('');
                  setLoanPeriodCount(4);
                }}
                className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auction Modal */}
      {showAuctionModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="football-card glass-football p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">Create Auction</h2>
            <div className="mb-4">
              <p className="text-gray-300 mb-2">{card.player?.full_name || 'Card'}</p>
              <label className="block text-gray-300 mb-2">Starting Price (DIOS)</label>
              <input
                type="number"
                value={auctionStartingPrice}
                onChange={(e) => setAuctionStartingPrice(e.target.value)}
                placeholder="Enter starting price"
                className="input-football w-full mb-4"
                min="0.01"
                step="0.01"
              />
              <label className="block text-gray-300 mb-2">Duration (Hours)</label>
              <div className="grid grid-cols-3 gap-3 mb-2">
                <div>
                  <select
                    value={auctionDurationHours}
                    onChange={(e) => setAuctionDurationHours(parseInt(e.target.value, 10))}
                    className="input-football w-full"
                  >
                    <option value={0}>0 Hours</option>
                    <option value={12}>12 Hours</option>
                    <option value={24}>24 Hours</option>
                    <option value={48}>48 Hours</option>
                    <option value={72}>72 Hours</option>
                  </select>
                </div>
                <div>
                  <input
                    type="number"
                    value={auctionDurationMinutes}
                    onChange={(e) => setAuctionDurationMinutes(parseInt(e.target.value, 10) || 0)}
                    min="0"
                    max="59"
                    className="input-football w-full"
                    placeholder="Minutes"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={auctionDurationSeconds}
                    onChange={(e) => setAuctionDurationSeconds(parseInt(e.target.value, 10) || 0)}
                    min="0"
                    max="59"
                    className="input-football w-full"
                    placeholder="Seconds"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">Minimum bid increment: 5% | +30 seconds per bid</p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleCreateAuction}
                disabled={isListing}
                className="btn-football flex-1"
              >
                {isListing ? 'Creating...' : 'Create Auction'}
              </button>
              <button
                onClick={() => {
                  setShowAuctionModal(false);
                  setAuctionStartingPrice('');
                  setAuctionDurationHours(24);
                  setAuctionDurationMinutes(0);
                  setAuctionDurationSeconds(0);
                }}
                className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trade Modal */}
      {showTradeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="football-card glass-football p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#1e293b] mb-4">Trade Card</h2>
            
            {hasOpenTrade && openTrade ? (
              // Card already has an open trade or counter offer - show remove option
              <div className="mb-4">
                {openTrade.isCounterOffer ? (
                  // Counter offer
                  <>
                    <p className="text-gray-300 mb-4">
                      This card is currently offered in a counter offer.
                    </p>
                    <div className="p-4 bg-yellow-500/10 border-2 border-yellow-500/30 rounded-lg mb-4">
                      <p className="text-sm text-gray-400 mb-1">Counter Offer for:</p>
                      <p className="font-semibold text-white">{openTrade.desiredPlayerName || 'Unknown Player'}</p>
                      <p className="text-xs text-gray-400">⭐ {openTrade.desiredRarity || 'Unknown Rarity'}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        Trade Owner: {openTrade.tradeOwnerUsername || 'Unknown'}
                      </p>
                    </div>
                    <p className="text-xs text-gray-400 mb-4">
                      Created: {openTrade.createdAt ? formatDate(openTrade.createdAt, {}, locale) : 'Unknown'}
                    </p>
                  </>
                ) : (
                  // Open trade
                  <>
                    <p className="text-gray-300 mb-4">
                      This card is currently available for trade.
                    </p>
                    <div className="p-4 bg-blue-500/10 border-2 border-blue-500/30 rounded-lg mb-4">
                      <p className="text-sm text-gray-400 mb-1">You want:</p>
                      <p className="font-semibold text-white">{openTrade.desiredPlayerName || 'Unknown Player'}</p>
                      <p className="text-xs text-gray-400">⭐ {openTrade.desiredRarity || 'Unknown Rarity'}</p>
                    </div>
                    <p className="text-xs text-gray-400 mb-4">
                      Trade expires: {openTrade.expiresAt ? formatDate(openTrade.expiresAt, {}, locale) : 'Unknown'}
                    </p>
                  </>
                )}
                <div className="flex space-x-4">
                  <button
                    onClick={handleRemoveTrade}
                    disabled={isListing}
                    className="btn-football flex-1 bg-red-600 hover:bg-red-700"
                  >
                    {isListing ? 'Removing...' : openTrade.isCounterOffer ? 'Remove Counter Offer' : 'Remove from Trade'}
                  </button>
                  <button
                    onClick={() => {
                      setShowTradeModal(false);
                    }}
                    className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // Card doesn't have a trade - show create form
              <>
                <div className="mb-4">
                  <p className="text-gray-300 mb-4">Your card: <strong>{card.player?.full_name || 'Card'}</strong></p>
                  
                  {/* Step 1: Search for Player */}
                  <div className="mb-4">
                    <label className="block text-gray-300 mb-2">What card do you want in return?</label>
                    <label className="block text-gray-300 mb-2 text-sm">Search Player</label>
                    <div className="flex space-x-2 mb-2">
                      <input
                        type="text"
                        value={tradeCardSearch}
                        onChange={(e) => setTradeCardSearch(e.target.value)}
                        placeholder="Enter player name"
                        className="input-football flex-1"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSearchPlayers();
                          }
                        }}
                      />
                      <button
                        onClick={handleSearchPlayers}
                        disabled={searchingPlayers}
                        className="btn-football px-4"
                      >
                        {searchingPlayers ? '...' : 'Search'}
                      </button>
                    </div>

                    {playerResults.length > 0 && (
                      <div className="max-h-40 overflow-y-auto space-y-2 mb-4">
                        {playerResults.map((player) => (
                          <div
                            key={player.id}
                            onClick={() => {
                              setSelectedPlayer(player);
                              setPlayerResults([]);
                              setTradeCardSearch(player.full_name || `${player.first_name} ${player.last_name}`);
                            }}
                            className={`p-2 rounded-lg border-2 cursor-pointer transition-all ${
                              selectedPlayer?.id === player.id
                                ? 'border-green-500 bg-green-500/20'
                                : 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
                            }`}
                          >
                            <p className="font-semibold text-white">{player.full_name || `${player.first_name} ${player.last_name}`}</p>
                            <p className="text-xs text-gray-400">{player.position || 'N/A'} • {player.team || 'N/A'}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedPlayer && (
                      <div className="mb-4">
                        <p className="text-gray-300 mb-2">Selected: <strong>{selectedPlayer.full_name || `${selectedPlayer.first_name} ${selectedPlayer.last_name}`}</strong></p>
                        <label className="block text-gray-300 mb-2">Select Rarity</label>
                        <select
                          value={selectedRarity}
                          onChange={(e) => setSelectedRarity(e.target.value)}
                          className="w-full p-3 rounded-lg border-2 border-[rgba(65,105,225,0.3)] bg-[#1e293b] text-white font-medium focus:border-[#4169E1] focus:outline-none focus:ring-2 focus:ring-[#4169E1]/50 transition-all appearance-none cursor-pointer"
                          style={{
                            color: '#ffffff',
                            backgroundColor: '#1e293b',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 1rem center',
                            paddingRight: '2.5rem'
                          }}
                        >
                          <option value="" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Select rarity...</option>
                          <option value="basic" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Basic</option>
                          <option value="plus" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Plus</option>
                          <option value="rare" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Rare</option>
                          <option value="epic" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Epic</option>
                          <option value="unique" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Unique</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-400 mt-4">
                    This will create an open trade. Anyone who has the requested card can accept it. Trade expires in 7 days.
                  </p>
                </div>
                <div className="flex space-x-4">
                  <button
                    onClick={handleCreateTrade}
                    disabled={isListing || !selectedPlayer || !selectedRarity}
                    className="btn-football flex-1"
                  >
                    {isListing ? 'Creating...' : 'Create Open Trade'}
                  </button>
                  <button
                    onClick={() => {
                      setShowTradeModal(false);
                      setTradeCardSearch('');
                      setSelectedPlayer(null);
                      setSelectedRarity('');
                      setPlayerResults([]);
                    }}
                    className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Info Modal - Rendered via Portal to ensure fullscreen */}
      {showInfoModal && createPortal(
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-[9999] flex items-center justify-center p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }} onClick={() => setShowInfoModal(false)}>
          <div className="football-card glass-football p-8 flex flex-col max-w-4xl w-full max-h-[90vh]" style={{ 
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '2px solid rgba(0, 217, 255, 0.5)',
            borderRadius: 0,
            clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))',
            boxShadow: '0 0 40px rgba(0, 217, 255, 0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6 flex-shrink-0 pb-4 border-b border-[#00D9FF]/40">
              <h2 className="text-3xl font-bold flex items-center" style={{ 
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#ffffff',
                textShadow: '0 0 15px rgba(0, 217, 255, 0.6), 0 2px 4px rgba(0, 0, 0, 0.8)'
              }}>
                <Info className="w-6 h-6 mr-3" style={{ color: '#00D9FF', filter: 'drop-shadow(0 0 5px rgba(0, 217, 255, 0.6))' }} />
                {disableProfileImageZoom ? (normalizedCardKind === 'legend' ? 'Legend' : 'Divinity') + ' Information' : 'Player Information'}
              </h2>
            </div>
            
            <div className="space-y-4 overflow-y-auto flex-1 pr-2 custom-scrollbar">
              {/* Legend/Divinity: Nome, Immagine, Bonus, Metodo acquisizione, Card ID, Numero/serie */}
              {disableProfileImageZoom && (
                <div className="space-y-4">
                  {/* Nome */}
                  <div className="p-4 transition-all" style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(0, 217, 255, 0.4)',
                    borderRadius: 0,
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                  }}>
                    <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 15px rgba(0, 217, 255, 0.8), 0 0 30px rgba(0, 217, 255, 0.4)' }}>Nome</div>
                    <div className="text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 0 10px rgba(255, 255, 255, 0.5), 0 2px 10px rgba(0, 0, 0, 1), 0 0 30px rgba(255, 255, 255, 0.2)' }}>
                      {cardWithNextMatch.customName || cardWithNextMatch.custom_name || cardWithNextMatch.player?.full_name || 'N/A'}
                    </div>
                  </div>
                  {/* Immagine */}
                  <div className="p-4 transition-all" style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(0, 217, 255, 0.4)',
                    borderRadius: 0,
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                  }}>
                    <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Immagine</div>
                    <div className="flex justify-center">
                      <CardImage
                        card={cardWithNextMatch}
                        alt={cardWithNextMatch.customName || cardWithNextMatch.custom_name || 'Card'}
                        className="w-40 h-48 object-cover rounded-lg border-2 border-[#00D9FF]/50"
                      />
                    </div>
                  </div>
                  {/* Bonus (ruolo + %) */}
                  {(cardWithNextMatch.scoreBonusPercent != null || cardWithNextMatch.scoreBonusPosition || cardWithNextMatch.scoreBonusLeague) && (
                    <div className="p-4 transition-all" style={{
                      background: 'rgba(0, 0, 0, 0.7)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(234, 179, 8, 0.5)',
                      borderRadius: 0,
                      clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                    }}>
                      <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#F59E0B', textShadow: '0 0 10px rgba(245, 158, 11, 0.5)' }}>Bonus</div>
                      <div className="space-y-1">
                        {cardWithNextMatch.scoreBonusPosition && (
                          <div className="text-lg font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>
                            Potenzia ruolo: <span style={{ color: '#22C55E' }}>{cardWithNextMatch.scoreBonusPosition}</span>
                          </div>
                        )}
                        {cardWithNextMatch.scoreBonusLeague && (
                          <div className="text-lg font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>
                            Potenzia lega: <span style={{ color: '#22C55E' }}>{cardWithNextMatch.scoreBonusLeague}</span>
                          </div>
                        )}
                        {cardWithNextMatch.scoreBonusPercent != null && (
                          <div className="text-lg font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>
                            Bonus score: <span style={{ color: '#22C55E' }}>+{Number(cardWithNextMatch.scoreBonusPercent)}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Metodo acquisizione + data */}
                  <div className="p-4 transition-all" style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 215, 0, 0.4)',
                    borderRadius: 0,
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                  }}>
                    <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#FFD700', textShadow: '0 0 10px rgba(255, 215, 0, 0.5)' }}>Metodo di acquisizione</div>
                    <div className="space-y-1">
                      <div className="text-lg font-bold capitalize" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1)' }}>
                        {({ pack: 'Pack', purchase: 'Acquisto', auction: 'Asta', trade: 'Scambio', loan: 'Prestito', initial: 'Iniziale' })[String(cardWithNextMatch.acquired_via || '')] || cardWithNextMatch.acquired_via || '—'}
                      </div>
                      {(cardWithNextMatch.acquired_at || card.acquired_at) && (
                        <div className="text-base" style={{ color: '#94A3B8' }}>
                          {formatDate(cardWithNextMatch.acquired_at || card.acquired_at, { day: '2-digit', month: '2-digit', year: 'numeric' }, locale)}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Prezzo di acquisto */}
                  {cardWithNextMatch.purchase_price != null && parseFloat(cardWithNextMatch.purchase_price) > 0 && (
                    <div className="p-4 transition-all" style={{
                      background: 'rgba(0, 0, 0, 0.7)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(234, 179, 8, 0.5)',
                      borderRadius: 0,
                      clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                    }}>
                      <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#F59E0B', textShadow: '0 0 10px rgba(245, 158, 11, 0.5)' }}>Prezzo di acquisto</div>
                      <div className="text-xl font-bold" style={{ color: '#FCD34D', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(234, 179, 8, 0.3)' }}>
                        {parseFloat(cardWithNextMatch.purchase_price).toFixed(2)} DIOS
                      </div>
                    </div>
                  )}
                  {/* Card ID + Numero/Serie */}
                  <div className="p-4 transition-all" style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(0, 217, 255, 0.4)',
                    borderRadius: 0,
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                  }}>
                    <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Card ID</div>
                    <div className="text-xl font-mono font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>
                      {cardWithNextMatch.id ?? card.id ?? 'N/A'}
                    </div>
                    {((cardWithNextMatch.copy_index ?? cardWithNextMatch.copyIndex) != null && (cardWithNextMatch.total_copies ?? cardWithNextMatch.totalCopies) != null) && (
                      <div className="text-lg font-bold mt-2" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>
                        {cardWithNextMatch.copy_index ?? cardWithNextMatch.copyIndex}/{cardWithNextMatch.total_copies ?? cardWithNextMatch.totalCopies}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Player Name - nascosto per legend/divinity custom */}
              {!disableProfileImageZoom && (
              <div className="p-4 transition-all" style={{
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0, 217, 255, 0.4)',
                borderRadius: 0,
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
              }}>
                <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 15px rgba(0, 217, 255, 0.8), 0 0 30px rgba(0, 217, 255, 0.4)' }}>Player Name</div>
                <div className="text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 0 10px rgba(255, 255, 255, 0.5), 0 2px 10px rgba(0, 0, 0, 1), 0 0 30px rgba(255, 255, 255, 0.2)' }}>{cardWithNextMatch.player?.full_name || 'N/A'}</div>
              </div>
              )}

              {/* Player-specific content (nascosto per legend/divinity) */}
              {!disableProfileImageZoom && (
              <>
              {/* Age */}
              <div className="p-4 transition-all" style={{
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0, 217, 255, 0.4)',
                borderRadius: 0,
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
              }}>
                <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 15px rgba(0, 217, 255, 0.8), 0 0 30px rgba(0, 217, 255, 0.4)' }}>Age</div>
                <div className="text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 0 10px rgba(255, 255, 255, 0.5), 0 2px 10px rgba(0, 0, 0, 1), 0 0 30px rgba(255, 255, 255, 0.2)' }}>
                  {playerAge !== null ? `${playerAge} years old` : 'N/A'}
                </div>
              </div>
              
              {/* Date of Birth */}
              <div className="p-4 transition-all" style={{
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0, 217, 255, 0.4)',
                borderRadius: 0,
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
              }}>
                <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 15px rgba(0, 217, 255, 0.8), 0 0 30px rgba(0, 217, 255, 0.4)' }}>Date of Birth</div>
                <div className="text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 0 10px rgba(255, 255, 255, 0.5), 0 2px 10px rgba(0, 0, 0, 1), 0 0 30px rgba(255, 255, 255, 0.2)' }}>{dateOfBirth}</div>
              </div>
              
              {/* Nationality */}
              <div className="p-4 transition-all" style={{
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0, 217, 255, 0.4)',
                borderRadius: 0,
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
              }}>
                <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 15px rgba(0, 217, 255, 0.8), 0 0 30px rgba(0, 217, 255, 0.4)' }}>Nationality</div>
                <div className="text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 0 10px rgba(255, 255, 255, 0.5), 0 2px 10px rgba(0, 0, 0, 1), 0 0 30px rgba(255, 255, 255, 0.2)' }}>{nationality}</div>
              </div>
              
              {/* Player Image */}
              {(getCustomPlayerImageUrl(cardWithNextMatch) || cardWithNextMatch.player?.image_path || cardWithNextMatch.player?.photo_url || cardWithNextMatch.image_url) && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(0, 217, 255, 0.4)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Player Image</div>
                  <div className="flex justify-center">
                    <CardImage
                      card={cardWithNextMatch}
                      alt={cardWithNextMatch.player?.full_name || 'Player'}
                      className="w-32 h-32 object-cover rounded-lg border-2 border-[#00D9FF]/50"
                    />
                  </div>
                </div>
              )}

              {/* Position */}
              {cardWithNextMatch.player?.position && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(0, 217, 255, 0.4)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Position</div>
                  <div className="text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>{positionToEnglish(cardWithNextMatch.player.position)}</div>
                  {cardWithNextMatch.player?.detailed_position && (
                    <div className="text-sm text-gray-400 mt-1">({cardWithNextMatch.player.detailed_position})</div>
                  )}
                </div>
              )}

              {/* Media ultimi voti (se disponibile) */}
              {!loadingPlayerScoreStats && playerScoreStats?.matchCount > 0 && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(34, 197, 94, 0.45)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#22C55E', textShadow: '0 0 10px rgba(34, 197, 94, 0.45)' }}>
                    Media ultimi {playerScoreStats.matchCount} voti
                  </div>
                  <div className="text-2xl font-bold text-emerald-400" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(34, 197, 94, 0.35)' }}>
                    {Number(playerScoreStats.average).toFixed(1)}
                  </div>
                  {playerScoreStats.scores?.length > 0 && (
                    <div className="text-xs text-gray-400 mt-2">
                      Ultimi: {playerScoreStats.scores.map((s) => Number(s).toFixed(1)).join(' · ')}
                    </div>
                  )}
                </div>
              )}

              {/* XP */}
              <div className="p-4 transition-all" style={{
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0, 217, 255, 0.4)',
                borderRadius: 0,
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
              }}>
                <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>XP</div>
                <div className="text-2xl font-bold text-green-400 mb-2" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(34, 197, 94, 0.3)' }}>{xpPercentage.toFixed(1)}%</div>
                <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, (xpPercentage / 15) * 100)}%` }}
                  />
                </div>
              </div>

              {/* Level */}
              <div className="p-4 transition-all" style={{
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0, 217, 255, 0.4)',
                borderRadius: 0,
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
              }}>
                <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Level</div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: maxLevel }).map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index < xpLevel ? 'bg-blue-400 shadow-lg shadow-blue-400/50 scale-110' : 'bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xl font-bold text-blue-400" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(59, 130, 246, 0.3)' }}>{xpLevel}/{maxLevel}</span>
                </div>
              </div>

              {/* Acquired */}
              {(cardWithNextMatch.acquired_at || card.acquired_at) && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(0, 217, 255, 0.4)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Acquired</div>
                  <div className="text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>
                    {formatDate(cardWithNextMatch.acquired_at || card.acquired_at, { day: '2-digit', month: '2-digit', year: 'numeric' }, locale)}
                  </div>
                </div>
              )}

              {/* Price */}
              {cardWithNextMatch.purchase_price != null && parseFloat(cardWithNextMatch.purchase_price) > 0 && parseFloat(cardWithNextMatch.purchase_price).toFixed(2) !== '0.00' && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(0, 217, 255, 0.4)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Price</div>
                  <div className="text-2xl font-bold text-yellow-400" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(234, 179, 8, 0.3)' }}>
                    {parseFloat(cardWithNextMatch.purchase_price).toFixed(2)} DIOS
                  </div>
                </div>
              )}

              {/* Physical Stats */}
              {(cardWithNextMatch.player?.height || cardWithNextMatch.player?.weight) && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(0, 217, 255, 0.4)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-3 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Physical Stats</div>
                  <div className="grid grid-cols-2 gap-4">
                    {cardWithNextMatch.player?.height && (
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Height</div>
                        <div className="text-lg font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>{cardWithNextMatch.player.height} cm</div>
                      </div>
                    )}
                    {cardWithNextMatch.player?.weight && (
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Weight</div>
                        <div className="text-lg font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>{cardWithNextMatch.player.weight} kg</div>
                      </div>
                    )}
                  </div>
                  {cardWithNextMatch.player?.gender && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-400 mb-1">Gender</div>
                      <div className="text-sm font-semibold capitalize" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>{cardWithNextMatch.player.gender}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Additional Info */}
              {(cardWithNextMatch.player?.common_name || cardWithNextMatch.player?.display_name || cardWithNextMatch.player?.jersey_number) && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(0, 217, 255, 0.4)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-3 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Additional Info</div>
                  <div className="space-y-2">
                    {cardWithNextMatch.player?.common_name && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">Common Name:</span>
                        <span className="text-base font-semibold text-white">{cardWithNextMatch.player.common_name}</span>
                      </div>
                    )}
                    {cardWithNextMatch.player?.display_name && cardWithNextMatch.player.display_name !== cardWithNextMatch.player.full_name && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">Display Name:</span>
                        <span className="text-base font-semibold text-white">{cardWithNextMatch.player.display_name}</span>
                      </div>
                    )}
                    {cardWithNextMatch.player?.jersey_number && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">Jersey Number:</span>
                        <span className="text-2xl font-bold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>#{cardWithNextMatch.player.jersey_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Team */}
              {cardWithNextMatch.player?.team && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(0, 217, 255, 0.4)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Team</div>
                  <div className="text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>{cardWithNextMatch.player.team}</div>
                  {cardWithNextMatch.player?.is_out_of_dios10 ? (
                    <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded bg-red-900/40 border border-red-700 text-red-300 text-xs font-semibold">
                      OUT OF DIOS
                    </div>
                  ) : null}
                </div>
              )}
              
              {/* League */}
              {cardWithNextMatch.player?.league && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(0, 217, 255, 0.4)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>League</div>
                  <div className="text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>{cardWithNextMatch.player.league}</div>
                </div>
              )}
              
              {/* Purchase Information */}
              {(cardWithNextMatch.acquired_at || (cardWithNextMatch.purchase_price && parseFloat(cardWithNextMatch.purchase_price) > 0)) && (
                <div className="p-4 transition-all" style={{
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 215, 0, 0.4)',
                  borderRadius: 0,
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}>
                  <div className="text-sm uppercase tracking-wider mb-3 font-semibold" style={{ color: '#FFD700', textShadow: '0 0 10px rgba(255, 215, 0, 0.5)' }}>Purchase Information</div>
                  <div className="space-y-2">
                    {cardWithNextMatch.acquired_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400 flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          Acquired Date:
                        </span>
                        <span className="text-lg font-bold text-white">
                          {formatDate(cardWithNextMatch.acquired_at, {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          }, locale)}
                        </span>
                      </div>
                    )}
                    {cardWithNextMatch.purchase_price && parseFloat(cardWithNextMatch.purchase_price) > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400 flex items-center">
                          <Wallet className="w-4 h-4 mr-2" />
                          Purchase Price:
                        </span>
                        <span className="text-lg font-bold text-yellow-400">
                          {parseFloat(cardWithNextMatch.purchase_price).toFixed(2)} DIOS
                        </span>
                      </div>
                    )}
                    {cardWithNextMatch.acquired_via && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">Acquired Via:</span>
                        <span className="text-lg font-semibold text-blue-400 capitalize">
                          {cardWithNextMatch.acquired_via}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Next Match */}
              <div className="p-4 transition-all" style={{
                background: 'rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0, 217, 255, 0.6)',
                borderRadius: 0,
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                boxShadow: '0 0 20px rgba(0, 217, 255, 0.3)'
              }}>
                <div className="text-sm uppercase tracking-wider mb-3 flex items-center justify-between font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>
                  <span className="flex items-center">
                    <Trophy className="w-5 h-5 mr-2" style={{ filter: 'drop-shadow(0 0 5px rgba(0, 217, 255, 0.6))' }} />
                    Matches
                  </span>
                  {loadingNextMatch && (
                    <span className="text-sm animate-pulse" style={{ color: '#00D9FF' }}>Loading...</span>
                  )}
                </div>
                {loadingNextMatch ? (
                  <div className="text-base flex items-center" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 mr-2" style={{ borderColor: '#00D9FF' }}></div>
                    Fetching match information...
                  </div>
                ) : (
                  <div className="space-y-4">
                    {inProgressMatch && (
                      <div className="pb-4" style={{ borderBottom: '1px solid rgba(0, 217, 255, 0.3)' }}>
                        <div className="text-sm font-bold mb-2 flex items-center gap-2" style={{ color: '#22C55E' }}>
                          <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                          Live
                        </div>
                        {inProgressMatch.competition && (
                          <div className="text-sm font-semibold px-3 py-1.5 mb-2" style={{ color: '#22C55E', background: 'rgba(34, 197, 94, 0.15)' }}>
                            {inProgressMatch.competition}
                          </div>
                        )}
                        <div className="flex items-center justify-center gap-3 py-2">
                          <span className="text-base font-bold flex-1 text-center" style={{ color: '#ffffff' }}>{inProgressMatch.homeTeam || 'TBD'}</span>
                          <span className="font-bold text-center min-w-[3rem]" style={{ color: '#22C55E' }}>
                            {inProgressMatch.homeScore != null && inProgressMatch.awayScore != null
                              ? `${inProgressMatch.homeScore} - ${inProgressMatch.awayScore}`
                              : 'vs'}
                          </span>
                          <span className="text-base font-bold flex-1 text-center" style={{ color: '#ffffff' }}>{inProgressMatch.awayTeam || 'TBD'}</span>
                        </div>
                      </div>
                    )}
                    {nextMatch && (
                      <div className="pb-4" style={{ borderBottom: (inProgressMatch || lastMatch) ? '1px solid rgba(0, 217, 255, 0.3)' : 'none' }}>
                        <div className="text-sm font-bold mb-2" style={{ color: '#00D9FF' }}>Next Match</div>
                        {isNotCalledUpMatch(nextMatch) ? (
                          <div
                            className="text-center text-lg font-bold px-4 py-6 rounded-lg"
                            style={{ color: '#F59E0B', background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.35)' }}
                          >
                            NON CONVOCATO
                          </div>
                        ) : (
                          <>
                            {nextMatch.competition && (
                              <div className="text-sm font-semibold px-3 py-1.5 mb-2" style={{ color: '#00D9FF', background: 'rgba(0, 217, 255, 0.15)' }}>
                                {nextMatch.competition}
                              </div>
                            )}
                            <div className="flex items-center justify-center gap-3 py-2">
                              <span className="text-base font-bold flex-1 text-center" style={{ color: '#ffffff' }}>{nextMatch.homeTeam || 'TBD'}</span>
                              <span className="font-bold" style={{ color: '#00D9FF' }}>vs</span>
                              <span className="text-base font-bold flex-1 text-center" style={{ color: '#ffffff' }}>{nextMatch.awayTeam || 'TBD'}</span>
                            </div>
                            {nextMatch.utcDate && (
                              <div className="text-sm flex items-center justify-center px-2 py-1.5" style={{ background: 'rgba(0, 0, 0, 0.5)', color: '#ffffff' }}>
                                <Calendar className="w-4 h-4 mr-1.5" style={{ color: '#00D9FF' }} />
                                {formatDateTime(nextMatch.utcDate, {}, locale)}
                              </div>
                            )}
                            {nextMatch.matchday && (
                              <div className="text-sm text-center mt-1" style={{ color: '#F59E0B' }}>Matchday {nextMatch.matchday}</div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {lastMatch && (
                      <div>
                        <div className="text-sm font-bold mb-2" style={{ color: '#94A3B8' }}>Last Match</div>
                        {lastMatch.competition && (
                          <div className="text-sm font-semibold px-3 py-1.5 mb-2" style={{ color: '#94A3B8', background: 'rgba(148, 163, 184, 0.15)' }}>
                            {lastMatch.competition}
                          </div>
                        )}
                        <div className="flex items-center justify-center gap-3 py-2">
                          <span className="text-base font-bold flex-1 text-center" style={{ color: '#ffffff' }}>{lastMatch.homeTeam || 'TBD'}</span>
                          <span className="font-bold text-center min-w-[3rem]" style={{ color: '#94A3B8' }}>
                            {lastMatch.homeScore != null && lastMatch.awayScore != null
                              ? `${lastMatch.homeScore} - ${lastMatch.awayScore}`
                              : 'vs'}
                          </span>
                          <span className="text-base font-bold flex-1 text-center" style={{ color: '#ffffff' }}>{lastMatch.awayTeam || 'TBD'}</span>
                        </div>
                        {lastMatch.utcDate && (
                          <div className="text-sm flex items-center justify-center px-2 py-1.5" style={{ background: 'rgba(0, 0, 0, 0.5)', color: '#94A3B8' }}>
                            <Calendar className="w-4 h-4 mr-1.5" />
                            {formatDate(lastMatch.utcDate, {}, locale)}
                          </div>
                        )}
                      </div>
                    )}
                    {!inProgressMatch && !nextMatch && !lastMatch && (
                      <div className="text-base text-center py-2" style={{ color: '#ffffff' }}>No match data available</div>
                    )}
                  </div>
                )}
              </div>

              {/* Card ID + progressivo (es. 3/10) - last info */}
              <div className="p-4 transition-all" style={{
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0, 217, 255, 0.4)',
                borderRadius: 0,
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
              }}>
                <div className="text-sm uppercase tracking-wider mb-2 font-semibold" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>Card ID</div>
                <div className="text-xl font-mono font-bold" style={{ color: '#ffffff', textShadow: '0 2px 8px rgba(0, 0, 0, 1), 0 0 20px rgba(255, 255, 255, 0.3)' }}>
                  {cardWithNextMatch.id ?? card.id ?? 'N/A'}
                </div>
                {((cardWithNextMatch.copy_index ?? cardWithNextMatch.copyIndex) != null && (cardWithNextMatch.total_copies ?? cardWithNextMatch.totalCopies) != null) && (
                  <div className="text-lg font-bold mt-2" style={{ color: '#00D9FF', textShadow: '0 0 10px rgba(0, 217, 255, 0.5)' }}>
                    {cardWithNextMatch.copy_index ?? cardWithNextMatch.copyIndex}/{cardWithNextMatch.total_copies ?? cardWithNextMatch.totalCopies}
                  </div>
                )}
              </div>
              </>
              )}
            </div>
            
            <button
              onClick={() => setShowInfoModal(false)}
              className="mt-6 w-full px-6 py-3 text-white font-bold transition-all flex-shrink-0 shadow-lg hover:shadow-xl transform hover:scale-105"
              style={{
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0, 217, 255, 0.5)',
                borderRadius: 0,
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontSize: '1.1rem',
                textShadow: '0 0 10px rgba(0, 217, 255, 0.5)'
              }}
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Message Display - in basso su mobile (evita navbar), in alto su desktop */}
      {message.text && (
        <div className={`app-toast fixed z-[90] bottom-4 left-4 right-4 sm:bottom-auto sm:top-24 sm:left-auto sm:right-4 p-4 rounded-lg shadow-xl backdrop-blur-md border-2 flex items-center justify-between gap-3 ${
          message.type === 'success'
            ? 'bg-green-600/85 border-green-400/80'
            : message.type === 'warning'
              ? 'bg-amber-500/85 border-amber-400/80 text-white'
              : 'bg-red-600/85 border-red-400/80 text-white'
        }`}>
          <span
            className={
              message.type === 'success'
                ? 'flex-1 font-medium platform-notification-text'
                : 'flex-1 font-medium text-white'
            }
          >
            {message.text}
          </span>
          <button
            type="button"
            onClick={() => setMessage({ type: '', text: '' })}
            className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-white/50 bg-white/20 hover:bg-white/30 text-white flex items-center justify-center text-lg leading-none transition-colors"
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default CardItem;
